/**
 * Was die Oberflaeche ueber den Discord-Server eines Micro-Credentials SAGT
 * -- an EINER Stelle (18.09.).
 *
 * Derselbe Bot haengt auf mehreren Discord-Servern. Welcher Server zu
 * welchem Credential gehoert, entscheidet der Admin (Tab "Discord-Server"
 * und der Credential-Dialog); der Professor sieht in der Kategorieauswahl,
 * WOHER seine Vorschlaege stammen. Beides sind Aussagen, und beide bekommen
 * hier einen NAMEN -- der Wortlaut daneben ist frei.
 *
 * Dieselbe Konvention wie hinweise.js (NEU_BEWERTEN), laden.js (LADEN),
 * shortcuts.js (KUERZEL): der Test prueft Schluessel und Bindung, nie die
 * Prosa.
 *
 * ACHTUNG, die Guild-ID ist eine ZEICHENKETTE und bleibt es auf dem ganzen
 * Weg: Snowflakes liegen ueber 2^53, und `Number()` verloere die letzte
 * Stelle, ohne dass irgendetwas rot wuerde. Wer hier parseInt einbaut,
 * verknuepft still einen anderen Server.
 */

import { getBekannteServer, getDiscordGuilds } from "@/api/admin";

/**
 * Die sieben Aussagen des Admin-Tabs und des Credential-Dialogs.
 *
 * keiner           -- am Credential haengt kein Server. STRIKT (01.10.):
 *                     die Sessions zeigen dann nur Nachrichten OHNE
 *                     Server-Angabe, und nur die eigener Teilnehmer --
 *                     nicht mehr "Name ueber alle Server" (das war ein Leck
 *                     in fremde Kurse)
 * ausweis          -- der Bot lehnt ab: MESSAGE_BOT_TOKEN passt nicht zum
 *                     API_TOKEN des Bots. NICHT "nicht erreichbar" -- wer
 *                     das liest, prueft Container und Netz und findet
 *                     dort nichts (Falle vom 24.08.)
 * nichtVerbunden   -- 503: der Bot laeuft, hat aber noch keine Gateway-
 *                     Sitzung. Ein Zeitpunkt, kein Fehler
 * nichtErreichbar  -- alles andere: Container, Netz, Konfiguration. Die
 *                     bekannten Server bleiben als Liste (01.10.)
 * handeingabe      -- ohne passenden Eintrag laesst sich die ID von Hand
 *                     eintragen (Discord: Entwicklermodus, Rechtsklick
 *                     auf den Server, "ID kopieren")
 * mehrfach         -- derselbe Server haengt an mehreren Credentials.
 *                     Zulaessig (Entscheidung 17.09.), aber sichtbar
 * abweichenderName -- der gespeicherte Servername ist aelter als der in
 *                     Discord. Der Name ist nur Anzeige, die ID entscheidet
 */
export const DISCORD_SERVER = {
  keiner: "Kein Discord-Server verknüpft — die Sessions zeigen dann nur Nachrichten ohne Server-Angabe der eigenen Teilnehmer.",
  ausweis: "Der Discord-Bot lehnt die Anfrage ab (kein gültiger Ausweis). MESSAGE_BOT_TOKEN in der Oberfläche muss dem API_TOKEN des Bots entsprechen.",
  nichtVerbunden: "Der Discord-Bot ist noch nicht verbunden — die Serverliste kommt, sobald seine Sitzung steht.",
  nichtErreichbar: "Der Discord-Bot ist nicht erreichbar — die Liste zeigt nur die bekannten Server; eine andere ID lässt sich von Hand eintragen.",
  handeingabe: "Server-ID von Hand (Discord: Entwicklermodus → Rechtsklick auf den Server → „Server-ID kopieren“).",
  mehrfach: "Dieser Server hängt an mehreren Credentials.",
  abweichenderName: "In Discord heißt der Server inzwischen anders — gespeichert ist der Name von der Verknüpfung; die ID entscheidet.",
};

/**
 * Die drei Aussagen der Kategorieauswahl darueber, WOHER die Vorschlaege
 * stammen. Sie sind Funktionen, weil Name und Zahl aus der Antwort kommen.
 *
 * einServer       -- verengt: nur die Kategorien des verknuepften Servers
 * alleServer      -- nicht verengt: das Credential hat keinen Server, die
 *                    Vorschlaege kommen aus allen N Servern des Bots. Die
 *                    SESSION zeigt trotzdem nur Nachrichten ohne
 *                    Server-Angabe (strikte Regel, 01.10.) -- der Satz sagt
 *                    das, sonst laese sich der Vorschlag als Zusage
 * serverUnbekannt -- verengt, aber der Bot ist auf diesem Server nicht
 *                    Mitglied: leere Liste MIT Grund
 */
export const VORSCHLAEGE_QUELLE = {
  einServer: (name) => `Discord-Vorschläge aus dem Server „${name}“ (dem Credential zugeordnet).`,
  alleServer: (n) => (n === 1
    ? "Kein Discord-Server am Credential — Vorschläge aus dem einzigen Server des Bots; die Session zeigt nur Nachrichten ohne Server-Angabe der eigenen Teilnehmer."
    : `Kein Discord-Server am Credential — Vorschläge aus allen ${n} Servern des Bots; die Session zeigt nur Nachrichten ohne Server-Angabe der eigenen Teilnehmer.`),
  serverUnbekannt: (id) => `Dem Credential ist der Server ${id} zugeordnet, aber der Bot ist dort nicht Mitglied — keine Discord-Vorschläge.`,
};

/**
 * Der Server-Bezug an einem Vorschlags-Chip (01.10.).
 *
 * Eine Zuordnung speichert nur den NAMEN; welcher Server gilt, entscheidet
 * der aktive Server des Credentials. Der Chip nennt deshalb die Herkunft
 * (fremde Server, auf denen der Name vorkommt) und sagt als TEXT, wenn die
 * Session unter dieser Regel nichts davon zeigen wuerde -- nicht nur ueber
 * die graue Farbe (WCAG 1.4.1).
 *
 * andererServer -- Server ohne Namen in der Antwort (fremder Kurs: sein
 *                  Name geht den Professor nichts an, Riegel M1)
 * unsichtbar    -- Chip-Zusatz: die Session zeigt davon nichts
 * unsichtbarWarum -- Tooltip dazu
 * ohneServer    -- Tooltip: Nachrichten ohne Server-Angabe (Altbestand)
 * sichtbar      -- Tooltip: die Zahl nach der Regel
 */
export const VORSCHLAG_SERVER = {
  andererServer: "anderer Server",
  unsichtbar: "hier unsichtbar",
  unsichtbarWarum: "Unter diesem Namen liegt nichts, was dieses Credential sieht — die Nachrichten stammen von einem anderen Server oder von Teilnehmern anderer Credentials. Zuordnen geht trotzdem; sichtbar wird es erst, wenn der Server dem Credential zugeordnet ist.",
  ohneServer: "ohne Server",
  sichtbar: (n) => `Für dieses Credential sichtbar: ${n}`,
};

/**
 * Welcher der drei Ausfallsaetze zu einem HTTP-Status gehoert.
 *
 * Dieselbe Dreiteilung wie in KategorieWahl.vue (24.08.): 401/403 ist ein
 * EIGENER Fall, weil er mit "nicht erreichbar" auf die falsche Faehrte
 * schickt; 503 ist ein Zeitpunkt; alles andere ist "nicht erreichbar".
 */
export function discordAusfallArt(status) {
  if (status === 401 || status === 403) return "ausweis";
  if (status === 503) return "nichtVerbunden";
  return "nichtErreichbar";
}

/**
 * Die Serverliste des Bots holen -- EINMAL, fuer beide Admin-Komponenten.
 *
 * Liefert { guilds, fehler }: guilds als [{ id, name }] mit id als
 * Zeichenkette, fehler als Schluessel aus DISCORD_SERVER oder "". Die
 * Komponenten rufen das hier auf, nicht in GuildWahl.vue -- sonst fragte
 * jede Tabellenzeile den Bot einzeln.
 */
export async function ladeDiscordServer() {
  try {
    const res = await getDiscordGuilds();
    const guilds = (res.data?.guilds || []).map((g) => ({
      id: String(g.id),
      name: g.name || "",
    }));
    return { guilds, fehler: "" };
  } catch (e) {
    return { guilds: [], fehler: discordAusfallArt(e?.response?.status) };
  }
}

/**
 * Die Server, die das System KENNT -- auch ohne Bot (01.10.). Quelle ist
 * GET /admin/bekannte-server: Serverhistorie, Credentials und die
 * guild_id der gespeicherten Nachrichten. Damit bleibt ein Server waehlbar,
 * den der Bot verlassen hat oder der gerade nicht antwortet -- sonst
 * bliebe nur die Handeingabe, und die ID muesste jemand aus Discord holen.
 *
 * Liefert { bekannte, fehler }: bekannte als [{ id, name, nachrichten }]
 * mit id als ZEICHENKETTE (s. Modulkopf), fehler true, wenn die Liste nicht
 * kam. Ein Ausfall ist hier kein eigener Satz: die Bot-Liste und die
 * Handeingabe bleiben, und GuildWahl entscheidet ueber alle Eintraege.
 */
export async function ladeBekannteServer() {
  try {
    const res = await getBekannteServer();
    const bekannte = (res.data?.server || []).map((s) => ({
      id: String(s.guild_id),
      name: s.guild_name || "",
      nachrichten: s.nachrichten || 0,
    }));
    return { bekannte, fehler: false };
  } catch (e) {
    console.warn("Bekannte Server nicht ladbar", e);
    return { bekannte: [], fehler: true };
  }
}

/**
 * Die Aussagen der SERVERWAHL (01.10.) -- GuildWahl und der Bestaetigungs-
 * dialog in beiden Admin-Komponenten.
 *
 * gruppe*       -- die drei Gruppen im Select. Die Labels muessen
 *                  verschieden sein (BaseSelect nimmt sie als :key)
 * eintragBekannt -- ein bekannter Server ohne Bot: Name (oder ID) und wie
 *                  viele Nachrichten dort gespeichert sind
 * titel         -- Kopf des Bestaetigungsdialogs
 * ersetzt       -- ein Server ersetzt einen anderen. EIN Satz, der die
 *                  Entscheidung traegt: der Wechsel gilt fuer ALLE Sessions,
 *                  auch beendete, und fuer das Feedback der Studierenden --
 *                  und NICHTS wird geloescht. Die Einzelheiten (eigener
 *                  Altbestand bleibt, Rueckwechsel) stehen am Icon
 *                  (ERKLAERUNG.serverErsetzen)
 * geloest       -- der Server wird entfernt, ohne neuen; danach gilt
 *                  dieselbe Regel wie DISCORD_SERVER.keiner
 * knopf*        -- die Bestaetigung im Dialog
 *
 * ACHTUNG, keine Schluessel, die Praefix eines anderen sind -- Tests suchen
 * per Teilstring (26.08.).
 */
export const SERVERWAHL = {
  gruppeKeiner: "Kein Server",
  gruppeBot: "Bot ist Mitglied",
  gruppeBekannt: "Bekannt – Bot nicht Mitglied",
  eintragBekannt: (name, id, n) => `${name || id} · ${n} Nachrichten`,
  titel: "Discord-Server ändern",
  ersetzt: (alt, neu) => `Alle Sessions – auch beendete – und das Feedback der Studierenden zeigen danach „${neu}“ statt „${alt}“. Nachrichten von „${alt}“ bleiben gespeichert.`,
  geloest: (alt) => `Alle Sessions – auch beendete – und das Feedback der Studierenden zeigen danach nur Nachrichten ohne Server-Angabe der eigenen Teilnehmer. Nachrichten von „${alt}“ bleiben gespeichert.`,
  knopfErsetzen: "Ersetzen",
  knopfLoesen: "Lösen",
};

/**
 * Braucht das Speichern eine Bestaetigung? EINE Stelle fuer beide Eltern
 * (Tab und Credential-Dialog), sonst fragte der eine und der andere nicht.
 *
 * Nur beim ERSETZEN oder LOESEN eines bestehenden Servers: dann zeigen die
 * Sessions danach etwas anderes. Ein erster Server (alt leer) und dieselbe
 * ID mit anderem Namen gehen ohne Rueckfrage durch.
 */
export function serverwechselBrauchtBestaetigung(alt, neu) {
  return !!alt && alt !== (neu || null);
}

/**
 * Eine Regel, die der Admin kennen muss und die keine Oberflaeche erzwingt.
 * Sie steht im Admin-Tab -- NICHT in GuildWahl, die auch im
 * Credential-Dialog steht und dort ohne Kontext waere.
 *
 * kurz          -- die sichtbare Zeile im Tab-Kopf (30.09.)
 * kategorieName -- das Warum dazu, am Info-Icon daneben
 */
export const SEMESTER_REGEL = {
  kurz: "Neues Semester = neues Credential, nicht Serverwechsel.",
  kategorieName: "Ein Kategoriename gilt nur innerhalb eines Servers. Wer im nächsten Semester dieselben Namen (TEAM1 …) verwendet, braucht dafür einen neuen Server — sonst zeigt die neue Session beide Jahrgänge.",
};
